import { cva, VariantProps } from 'class-variance-authority';
import React from 'react';

import { WithLoading } from '@/ui/WithLoading';
import { cn } from '@/utils';

const inputVariants = cva(
	'flex rounded-lg border border-brand-gray-4 bg-white font-sans ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-brand-gray-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
	{
		variants: {
			variant: {
				sm: 'box-border !h-[2.5em] !min-h-[2.5em] !max-h-[2.5em] !border-[var(--color-gray3)] px-[0.75em] !py-0 text-base leading-tight placeholder-shown:text-brand-gray-3',
				md: 'h-9 px-3 py-[10px] text-base leading-tight',
			},
			startIcon: {
				true: 'pl-[2.5em] pr-[1em]',
			},
			endIcon: {
				true: 'pr-[2.5em]',
			},
			startEndIcon: {
				true: 'pl-[2.5em] pr-[2.5em]',
			},
		},
		defaultVariants: {
			variant: 'md',
			startIcon: false,
			endIcon: false,
		},
	}
);

export interface InputProps
	extends Omit<VariantProps<typeof inputVariants>, 'startIcon' | 'endIcon'>,
		React.InputHTMLAttributes<HTMLInputElement> {
	rootClassName?: string;
	startIcon?: React.ReactNode;
	endIcon?: React.ReactNode;
	hint?: string;
	isLoading?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>((props, ref) => {
	const {
		rootClassName,
		className = '',
		type,
		variant = 'md',
		startIcon,
		endIcon,
		isLoading = false,
		hint = '',
		value,
		placeholder,
		title,
		...restProps
	} = props;
	const hasValueProp = Object.prototype.hasOwnProperty.call(props, 'value');
	const inputProps = hasValueProp ? { ...restProps, value: value ?? '' } : restProps;
	const inputTitle =
		title ?? (variant === 'sm' && hasValueProp && !value && typeof placeholder === 'string' ? placeholder : '');

	const onWheel = (event: React.WheelEvent<HTMLInputElement>) => {
		if (type === 'number') {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			// eslint-disable-next-line @typescript-eslint/no-unsafe-call
			event.target.blur();
		}
	};

	return (
		<div className={cn('relative inline-block w-full', rootClassName)}>
			{startIcon && (
				<div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-[0.75em] text-base">
					<span className="text-brand-gray-2 flex size-[1em] items-center">{startIcon}</span>
				</div>
			)}
			<input
				type={type}
				className={inputVariants({
					variant,
					endIcon: !!endIcon && !startIcon,
					startIcon: !!startIcon && !endIcon,
					startEndIcon: !!startIcon && !!endIcon,
					className: `${className} w-full truncate`,
				})}
				ref={ref}
				placeholder={placeholder}
				title={inputTitle}
				autoComplete="off"
				onWheel={onWheel}
				{...inputProps}
			/>
			{(isLoading || endIcon) && (
				<div className="absolute inset-y-0 right-0 flex items-center pr-[0.75em] text-base">
					<span className="text-brand-gray-2 flex size-[1.5em] items-center">
						<WithLoading isLoading={isLoading} className="w-full h-full">
							{endIcon}
						</WithLoading>
					</span>
				</div>
			)}
			{hint && (
				<div className="pointer-events-none absolute inset-y-11 left-0 flex items-center pl-3">
					<span className="text-brand-gray-3 text-sm h-4 w-4">{hint}</span>
				</div>
			)}
		</div>
	);
});

Input.displayName = 'Input';

export { Input };
